/**
 * SOLID Review
 * S — Strong. One concern: what the `ClientUseCases` socket answers while nothing is connected to it.
 * O — Strong. A contract op added later fails the contract-coverage test, not a cast.
 * L — Strong. Implements `IPortClientUseCases` exactly: every op, the same answer.
 * I — Strong. The contract's ops and nothing else.
 * D — Strong. The kernel's failure builder and the port's interface.
 */
import { portFailure } from '@ml3d/kernel';
import type { IPortClientUseCases } from './ClientUseCases.contract';

/**
 * THE `ClientUseCases` SOCKET'S NULL OBJECT (frontend-application-oo P6, 2026-10-28; `unavailable()` built it reflectively until
 * then). The frontend's failure policy (strict-ports FE1): a socket the assembly never connected degrades the move that needed
 * it — every op answers `UNAVAILABLE` — and the rest of the app still renders. The kernel guards it on the same terms as a real
 * provider, which is why it answers in the contract's own Result shape. The production-wiring test, not this, keeps an
 * unconnected socket from shipping: it asserts `assertAssembled` with an empty allowlist.
 */
export class UnavailableClientUseCases implements IPortClientUseCases {
  private readonly failure = portFailure('UNAVAILABLE', 'ClientUseCases is not connected.');

  async startGame() {
    return this.failure;
  }

  async rollDice() {
    return this.failure;
  }

  async peekDice() {
    return this.failure;
  }

  async announce() {
    return this.failure;
  }

  async believe() {
    return this.failure;
  }

  async liftCup() {
    return this.failure;
  }

  async rollFirst() {
    return this.failure;
  }

  async playAgain() {
    return this.failure;
  }

  async leaveTable() {
    return this.failure;
  }

  async loadLobbyRoom() {
    return this.failure;
  }

  async lobbyPollTick() {
    return this.failure;
  }

  async watchTable() {
    return this.failure;
  }

  async unwatchTable() {
    return this.failure;
  }

  async startSession() {
    return this.failure;
  }

  async enterGame() {
    return this.failure;
  }

  async createGuest() {
    return this.failure;
  }

  async checkTableCode() {
    return this.failure;
  }

  async navigate() {
    return this.failure;
  }

  async dismissJoinError() {
    return this.failure;
  }

  async requestLoginCode() {
    return this.failure;
  }

  async signUp() {
    return this.failure;
  }

  async verifyCode() {
    return this.failure;
  }

  async checkReferral() {
    return this.failure;
  }

  async loadProfile() {
    return this.failure;
  }

  async changeNickname() {
    return this.failure;
  }

  async requestEmailChange() {
    return this.failure;
  }

  async verifyEmailChange() {
    return this.failure;
  }

  async signOut() {
    return this.failure;
  }

  async loadLegalDocuments() {
    return this.failure;
  }

  async checkLegalReview() {
    return this.failure;
  }

  async acceptLegalTerms() {
    return this.failure;
  }

  async exportMyData() {
    return this.failure;
  }

  async requestAccountDeletion() {
    return this.failure;
  }

  async deleteAccount() {
    return this.failure;
  }

  async startConsent() {
    return this.failure;
  }

  async openCookiePreferences() {
    return this.failure;
  }

  async saveCookiePreferences() {
    return this.failure;
  }

  async startCheckout() {
    return this.failure;
  }

  async readBalance() {
    return this.failure;
  }

  async watchAccount() {
    return this.failure;
  }

  async unwatchAccount() {
    return this.failure;
  }

  async loadAdConfig() {
    return this.failure;
  }

  async checkServerHealth() {
    return this.failure;
  }

  async toggleMedia() {
    return this.failure;
  }

  async reconnectMedia() {
    return this.failure;
  }

  async checkMediaIdle() {
    return this.failure;
  }

  async mediaRemovedByServer() {
    return this.failure;
  }

  async dismissMediaBanner() {
    return this.failure;
  }

  async mediaUsed() {
    return this.failure;
  }

  async mediaToken() {
    return this.failure;
  }

  async reportMediaConnection() {
    return this.failure;
  }

  async checkMediaHealth() {
    return this.failure;
  }

  async loadMediaUsageReport() {
    return this.failure;
  }

  async reportClientError() {
    return this.failure;
  }
}
